import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Trophy, Check, X } from 'lucide-react';
import { useRecentGameResults } from '@/hooks/useRecentGameResults';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';

interface RecentResultsSectionProps {
  stateProvince: string;
  country?: string;
  sportFilter?: string;
}

const RecentResultsSection: React.FC<RecentResultsSectionProps> = ({
  stateProvince,
  country = 'United States',
  sportFilter,
}) => {
  const { results, loading, error } = useRecentGameResults({
    stateProvince,
    country,
    sportFilter,
    limit: 10,
  });

  if (loading) {
    return (
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-foreground">
            <Trophy className="h-5 w-5 text-primary" />
            Recent Results
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-foreground">
            <Trophy className="h-5 w-5 text-primary" />
            Recent Results
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Unable to load results. Please try again later.</p>
        </CardContent>
      </Card>
    );
  }

  const hasResults = results.length > 0;

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-foreground">
          <Trophy className="h-5 w-5 text-primary" />
          Recent Results
          {hasResults && (
            <Badge variant="secondary" className="ml-2">
              Last 7 days
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!hasResults ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">No completed games in the past week.</p>
            <p className="text-sm text-muted-foreground mt-1">Check back after some games finish!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {results.map(result => (
              <Link 
                key={result.id} 
                to={`/games/${result.id}/details`}
                className="block"
              >
                <div className="p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(result.game_date), 'MMM d, yyyy')}
                    </span>
                    {result.sport_name && (
                      <Badge variant="outline" className="text-xs">{result.sport_name}</Badge>
                    )}
                  </div>
                  
                  {/* Matchup with scores */}
                  <div className="space-y-2">
                    {/* Home Team */}
                    <div className={`flex items-center justify-between ${result.is_home_winner ? 'text-primary' : 'text-muted-foreground'}`}>
                      <div className="flex items-center gap-2">
                        {result.is_home_winner ? (
                          <Check className="h-4 w-4 text-primary" />
                        ) : (
                          <X className="h-4 w-4 text-destructive" />
                        )}
                        <span className={`font-medium ${result.is_home_winner ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {result.home_team_name}
                        </span>
                        {result.is_home_winner && (
                          <Badge className="bg-primary/20 text-primary text-xs">W</Badge>
                        )}
                      </div>
                      <span className={`text-lg font-bold ${result.is_home_winner ? 'text-primary' : 'text-muted-foreground'}`}>
                        {result.home_score}
                      </span>
                    </div>
                    
                    {/* Away Team */}
                    <div className={`flex items-center justify-between ${!result.is_home_winner ? 'text-primary' : 'text-muted-foreground'}`}>
                      <div className="flex items-center gap-2">
                        {!result.is_home_winner ? (
                          <Check className="h-4 w-4 text-primary" />
                        ) : (
                          <X className="h-4 w-4 text-destructive" />
                        )}
                        <span className={`font-medium ${!result.is_home_winner ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {result.away_team_name}
                        </span>
                        {!result.is_home_winner && (
                          <Badge className="bg-primary/20 text-primary text-xs">W</Badge>
                        )}
                      </div>
                      <span className={`text-lg font-bold ${!result.is_home_winner ? 'text-primary' : 'text-muted-foreground'}`}>
                        {result.away_score}
                      </span>
                    </div>
                  </div>
                  
                  <div className="text-xs text-muted-foreground mt-2 text-center">
                    Final: {result.home_score} - {result.away_score}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentResultsSection;
